// SPDX-License-Identifier: GPL-3.0-or-later
//
// Extract plain text from an uploaded CV file. Dispatches on extension /
// MIME and lazy-loads the heavy parsers (pdfjs-dist, mammoth) so they don't
// bloat the initial bundle for users who never import a CV.

export interface CvParseResult {
  text: string;
  // Source kind for downstream prompts ("the user uploaded a PDF/DOCX/…").
  kind: 'pdf' | 'docx' | 'html' | 'text';
  // Page count for PDFs, undefined otherwise. Helps the AI know whether to
  // expect headers/footers in the raw text.
  pageCount?: number;
}

export type CvParseKind = CvParseResult['kind'];

const PDF_EXT = /\.pdf$/i;
const DOCX_EXT = /\.docx$/i;
const HTML_EXT = /\.(html?|xhtml)$/i;
const TEXT_EXT = /\.(txt|md|markdown|rst)$/i;

function detectKind(file: File): CvParseKind | null {
  const name = file.name;
  if (PDF_EXT.test(name) || file.type === 'application/pdf') return 'pdf';
  if (
    DOCX_EXT.test(name) ||
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'docx';
  }
  if (HTML_EXT.test(name) || file.type === 'text/html') return 'html';
  if (TEXT_EXT.test(name) || file.type.startsWith('text/')) return 'text';
  return null;
}

export function isSupportedCvFile(file: File): boolean {
  return detectKind(file) !== null;
}

export const SUPPORTED_CV_ACCEPT =
  '.pdf,.docx,.txt,.md,.markdown,.html,.htm,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/html';

export async function parseCvFile(file: File): Promise<CvParseResult> {
  const kind = detectKind(file);
  if (!kind) {
    throw new Error(
      `Unsupported file type: ${file.name}. Use PDF, DOCX, TXT, MD or HTML.`,
    );
  }
  switch (kind) {
    case 'pdf':
      return parsePdf(file);
    case 'docx':
      return parseDocx(file);
    case 'html':
      return parseHtml(file);
    case 'text':
      return parseText(file);
  }
}

// PDF.js is lazy-loaded; the worker is colocated with the .mjs entry point and
// pulled in as an asset URL by Vite. Setting workerSrc once is enough for the
// session — pdfjs caches it on the global GlobalWorkerOptions.
let pdfWorkerInitialised = false;
async function loadPdfJs(): Promise<typeof import('pdfjs-dist')> {
  const pdfjs = await import('pdfjs-dist');
  if (!pdfWorkerInitialised) {
    // Vite resolves this to a hashed URL of the worker script at build time.
    const workerUrl = (
      await import('pdfjs-dist/build/pdf.worker.min.mjs?url')
    ).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    pdfWorkerInitialised = true;
  }
  return pdfjs;
}

async function parsePdf(file: File): Promise<CvParseResult> {
  const pdfjs = await loadPdfJs();
  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(buf) }).promise;
  const parts: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    // pdfjs returns items with str + a transform matrix. Group items by y
    // coordinate so single visual lines stay together; PDFs frequently emit
    // text in column order rather than reading order.
    let lastY: number | null = null;
    const lineBuf: string[] = [];
    const lines: string[] = [];
    for (const it of content.items) {
      if (!('str' in it)) continue;
      const item = it as { str: string; transform: number[]; hasEOL?: boolean };
      const y = Math.round(item.transform[5] ?? 0);
      if (lastY === null || Math.abs(y - lastY) > 2) {
        if (lineBuf.length > 0) lines.push(lineBuf.join(' '));
        lineBuf.length = 0;
        lastY = y;
      }
      const s = item.str.trim();
      if (s) lineBuf.push(s);
      if (item.hasEOL) {
        if (lineBuf.length > 0) lines.push(lineBuf.join(' '));
        lineBuf.length = 0;
        lastY = null;
      }
    }
    if (lineBuf.length > 0) lines.push(lineBuf.join(' '));
    parts.push(lines.join('\n'));
  }
  await doc.cleanup();
  return {
    text: parts.join('\n\n').trim(),
    kind: 'pdf',
    pageCount: doc.numPages,
  };
}

async function parseDocx(file: File): Promise<CvParseResult> {
  // Mammoth ships a CJS browser bundle; Vite handles the interop. We use
  // extractRawText rather than convertToHtml because the AI does better on
  // clean prose than on noisy nested markup.
  const mod = (await import('mammoth')) as unknown as {
    default?: { extractRawText: (opts: { arrayBuffer: ArrayBuffer }) => Promise<{ value: string }> };
    extractRawText?: (opts: { arrayBuffer: ArrayBuffer }) => Promise<{ value: string }>;
  };
  const extractRawText = mod.extractRawText ?? mod.default?.extractRawText;
  if (!extractRawText) throw new Error('Failed to load DOCX parser.');
  const buf = await file.arrayBuffer();
  const out = await extractRawText({ arrayBuffer: buf });
  return { text: out.value.trim(), kind: 'docx' };
}

async function parseHtml(file: File): Promise<CvParseResult> {
  const html = await file.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Strip script/style nodes so their contents don't bleed into the profile.
  doc.querySelectorAll('script, style, noscript').forEach((n) => n.remove());
  const text = (doc.body?.innerText ?? doc.body?.textContent ?? '').trim();
  return { text, kind: 'html' };
}

async function parseText(file: File): Promise<CvParseResult> {
  return { text: (await file.text()).trim(), kind: 'text' };
}
